import {
  Balance,
  Transaction,
  TransactionFilters,
  TransactionReq,
  TransactionsMonthlyFinanceStats,
} from "../../interfaces/services/Finance";
import apiUrls from "../../store/apiUrls";
import { toFixedISOString } from "../../utils/dateUtils";
import apiService, { apiCachedGet, invalidateCachePrefix } from "./apiService";

const FIN_NS = "FIN:";

const CK = {
  txBySalon: (salonCode: string, filters?: TransactionFilters) =>
    `${FIN_NS}tx:salon:${salonCode}:${JSON.stringify(filters ?? {})}`,

  balanceBySalon: (salonCode: string) => `${FIN_NS}balance:${salonCode}`,

  monthly: (salonCode: string, year: number, month: number) =>
    `${FIN_NS}monthly:${salonCode}:${year}-${month}`,
  stats: (year: number, month: number) => `${FIN_NS}monthly:${year}-${month}`,
};

const financeService = {
  getTransactionsWithSalonCode: (
    salonCode: string,
    filters?: TransactionFilters
  ): Promise<Transaction[]> => {
    const params = new URLSearchParams();
    if (filters) {
      if (filters.employeeCode)
        params.append("employeeCode", filters.employeeCode);
      if (filters.productCode)
        params.append("productCode", filters.productCode);
      if (filters.category) params.append("category", filters.category);
      if (filters.type) params.append("type", filters.type);
      if (filters.status !== undefined)
        params.append("status", String(filters.status));
      if (filters.startDate) params.append("startDate", filters.startDate);
      if (filters.endDate) params.append("endDate", filters.endDate);
    }

    const url = `${
      apiUrls.finance.transaction.getBySalon
    }/${salonCode}?${params.toString()}`;

    return apiCachedGet<Transaction[]>("FINANCE", url, {
      ttl: 2 * 60 * 1000,
      keyExtra: CK.txBySalon(salonCode, filters),
    });
  },

  getBalanceWithSalonCode: (salonCode: string): Promise<Balance> => {
    const url = `${apiUrls.finance.balance.getBySalon}/${salonCode}`;
    return apiCachedGet<Balance>("FINANCE", url, {
      ttl: 1 * 60 * 1000,
      keyExtra: CK.balanceBySalon(salonCode),
    });
  },

  getFilteredTransactions: (
    salonCode: string,
    filters: TransactionFilters = {}
  ): Promise<Transaction[]> => {
    const params = new URLSearchParams();
    if (filters.employeeCode)
      params.append("employeeCode", filters.employeeCode);
    if (filters.productCode) params.append("productCode", filters.productCode);
    if (filters.category) params.append("category", filters.category);
    if (filters.type) params.append("type", filters.type);
    if (filters.status !== undefined)
      params.append("status", filters.status.toString());
    if (filters.startDate) params.append("startDate", filters.startDate);
    if (filters.endDate) params.append("endDate", filters.endDate);

    const url =
      `${apiUrls.finance.transaction.getBySalon}/${salonCode}` +
      (params.toString() ? `?${params}` : "");

    return apiCachedGet<Transaction[]>("FINANCE", url, {
      keyExtra: CK.txBySalon(salonCode, filters),
    });
  },

  getMonthlyReport: (
    salonCode: string,
    year: number,
    month: number
  ): Promise<any> => {
    const url = `${apiUrls.finance.transaction.monthlyReport}?salonCode=${salonCode}&year=${year}&month=${month}`;

    return apiCachedGet<any>("FINANCE", url, {
      keyExtra: CK.monthly(salonCode, year, month),
    });
  },

  /* -------------------- MUTATIONS (invalidate) -------------------- */

  createTransaction: async (transaction: TransactionReq): Promise<string> => {
    const res = await apiService.FINANCE.post(
      `${apiUrls.finance.transaction.transaction}`,
      transaction
    );
    invalidateCachePrefix(FIN_NS);
    return res;
  },

  changeTransactionStatus: async (code: string): Promise<any> => {
    const dateNow = new Date();
    const res = await apiService.FINANCE.patch(
      `${apiUrls.finance.transaction.transaction}/${code}/status`,
      { status: true, paidDate: toFixedISOString(dateNow) }
    );
    invalidateCachePrefix(FIN_NS);
    return res;
  },

  deleteTransaction: async (code: string): Promise<any> => {
    const res = await apiService.FINANCE.delete(
      `${apiUrls.finance.transaction.transaction}/${code}`
    );
    invalidateCachePrefix(FIN_NS);
    return res;
  },

  monthlyFinanceStats: async (
    year: number,
    month: number
  ): Promise<TransactionsMonthlyFinanceStats> => {
    const p = new URLSearchParams();
    p.append("year", year.toString());
    p.append("month", month.toString());

    const url = `${apiUrls.finance.transaction.monthlyStats}?${p}`;
    return apiCachedGet<TransactionsMonthlyFinanceStats>("FINANCE", url, {
      keyExtra: CK.stats(year, month),
    });
  },
};

export default financeService;
